/**
 * Project price entries for the line tables (owner 2026-10-27).
 *
 * One batch call (`GET /project-prices?projectIds=`) for the ids not already
 * cached, plus the price-tier master (fetched once per page load). The cache is
 * module-level and keyed by project id, so several tables on one page share it.
 * `invalidateProjectPrices(projectId)` drops a project (call it after a
 * `PUT /projects/:id/prices`); mounted hooks refetch it.
 *
 * Pricing rules live in `lib/project-pricing.ts` / `lib/line-pricing.ts`.
 */

import { useEffect, useMemo, useState } from 'react';
import { api } from './api';
import type { ProjectPriceEntry } from './project-pricing';
import type { PriceTier } from './types';

const BATCH_LIMIT = 100;

const entryCache = new Map<number, ProjectPriceEntry[]>();
let tiersCache: PriceTier[] | null = null;
let tiersPromise: Promise<PriceTier[]> | null = null;
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

/** Drop one project's cached entries (or all, with no argument) and refetch in mounted hooks. */
export function invalidateProjectPrices(projectId?: number): void {
  if (projectId === undefined) entryCache.clear();
  else entryCache.delete(projectId);
  notify();
}

/** Alias of `invalidateProjectPrices`. */
export const invalidate = invalidateProjectPrices;

/** Put a project's stored set straight into the cache (e.g. the `PUT /projects/:id/prices` response). */
export function primeProjectPrices(projectId: number, entries: ProjectPriceEntry[]): void {
  entryCache.set(projectId, entries);
  notify();
}

function loadTiers(): Promise<PriceTier[]> {
  if (tiersCache !== null) return Promise.resolve(tiersCache);
  if (tiersPromise === null) {
    tiersPromise = api
      .listPriceTiers()
      .then((res) => {
        tiersCache = [...res.data].sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id);
        return tiersCache;
      })
      .catch((err: unknown) => {
        tiersPromise = null;
        throw err;
      });
  }
  return tiersPromise;
}

async function loadEntries(projectIds: number[], signal: AbortSignal): Promise<void> {
  for (let i = 0; i < projectIds.length; i += BATCH_LIMIT) {
    const chunk = projectIds.slice(i, i + BATCH_LIMIT);
    const res = await api.projectPricesBatch(chunk, signal);
    const grouped = new Map<number, ProjectPriceEntry[]>(chunk.map((id) => [id, []]));
    for (const { projectId, ...entry } of res.data.entries) {
      grouped.get(projectId)?.push(entry);
    }
    for (const [id, entries] of grouped) entryCache.set(id, entries);
  }
}

export interface ProjectPricesState {
  entriesByProject: Map<number, ProjectPriceEntry[]>;
  tiers: PriceTier[];
  defaultTierId: number | null;
  loading: boolean;
  error: Error | null;
}

export function useProjectPrices(projectIds: readonly number[]): ProjectPricesState {
  const key = useMemo(
    () => [...new Set(projectIds.filter((id) => Number.isSafeInteger(id) && id > 0))].sort((a, b) => a - b).join(','),
    [projectIds],
  );
  // `invalidations` re-runs the fetch; `loadedTick` re-reads the cache after a load.
  const [invalidations, setInvalidations] = useState(0);
  const [loadedTick, setLoadedTick] = useState(0);
  const [tiers, setTiers] = useState<PriceTier[]>(() => tiersCache ?? []);
  const [failure, setFailure] = useState<{ key: string; error: Error } | null>(null);

  useEffect(() => {
    const listener = () => setInvalidations((v) => v + 1);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    const ids = key === '' ? [] : key.split(',').map(Number);
    const missing = ids.filter((id) => !entryCache.has(id));
    const needTiers = tiersCache === null;
    if (missing.length === 0 && !needTiers) return () => controller.abort();
    Promise.all([loadTiers(), missing.length > 0 ? loadEntries(missing, signal) : Promise.resolve()])
      .then(([loadedTiers]) => {
        if (signal.aborted) return;
        setTiers(loadedTiers);
        setFailure(null);
        setLoadedTick((v) => v + 1);
      })
      .catch((err: unknown) => {
        if (signal.aborted) return;
        setFailure({ key, error: err instanceof Error ? err : new Error(String(err)) });
      });
    return () => controller.abort();
  }, [key, invalidations]);

  const error = failure !== null && failure.key === key ? failure.error : null;
  const loading =
    error === null &&
    (tiersCache === null || (key !== '' && key.split(',').some((id) => !entryCache.has(Number(id)))));

  const entriesByProject = useMemo(() => {
    void invalidations;
    void loadedTick;
    const map = new Map<number, ProjectPriceEntry[]>();
    if (key === '') return map;
    for (const id of key.split(',').map(Number)) {
      const entries = entryCache.get(id);
      if (entries !== undefined) map.set(id, entries);
    }
    return map;
  }, [key, invalidations, loadedTick]);

  const defaultTierId = useMemo(() => tiers.find((t) => t.isDefault)?.id ?? null, [tiers]);

  return { entriesByProject, tiers, defaultTierId, loading, error };
}
